import React from 'react'
import { 
  FiX, 
  FiDollarSign, 
  FiTag, 
  FiRefreshCw, 
  FiSave,
  FiLoader,
  FiAlertCircle,
  FiCheckCircle,
  FiArrowUp,
  FiUser,
  FiCreditCard
} from "react-icons/fi";
import { 
  MdOutlineClose, 
  MdOutlineAttachMoney,
  MdOutlinePerson,
  MdOutlineReceipt,
  MdOutlineRefresh,
  MdOutlineSave,
  MdOutlinePayment
} from "react-icons/md";
import { IoCloseOutline, IoCashOutline, IoCardOutline, IoRefreshOutline } from "react-icons/io5";
import InputField from '../../../components/ui/Input'

const RefundModal = ({ 
  title, 
  onCancel, 
  inputValue, 
  onSubmit, 
  handleRefundOnChange,
  loading = false 
}) => {
  // Check if form is valid
  const isFormValid = inputValue?.amount && parseFloat(inputValue.amount) > 0 && inputValue?.id?.trim();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl animate-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl">
              <FiArrowUp className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">{title || "Refund Payment"}</h3>
              <p className="text-xs text-gray-400 hidden sm:block">
                Process a refund for this transaction
              </p>
            </div>
          </div>
          <button
            aria-label="Close modal"
            className="flex items-center justify-center w-9 h-9 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
            onClick={onCancel}
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Transaction Info (if available) */}
          {inputValue?.id && (
            <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-xl">
              <FiTag className="w-4 h-4 text-blue-500 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-blue-600 font-medium">Transaction ID</p>
                <p className="text-sm font-mono text-gray-700 truncate">
                  {inputValue.id}
                </p>
              </div>
            </div>
          )}

          {/* Refund Amount */}
          <div>
            <label className="flex items-center gap-2 mb-1.5 text-sm font-medium text-gray-700">
              <FiDollarSign className="w-4 h-4 text-gray-400" />
              <span>Refund Amount</span>
              <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <IoCashOutline className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="number"
                name="amount"
                value={inputValue?.amount || ""}
                onChange={handleRefundOnChange}
                placeholder="Enter refund amount..."
                min="0.01"
                step="0.01"
                className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#715EFE]/20 focus:border-[#715EFE] transition-all duration-200"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">USD</span>
            </div>
          </div>

          {/* Transaction ID */}
          <div>
            <label className="flex items-center gap-2 mb-1.5 text-sm font-medium text-gray-700">
              <FiTag className="w-4 h-4 text-gray-400" />
              <span>Transaction ID</span>
              <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <FiCreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                name="id"
                value={inputValue?.id || ""}
                onChange={handleRefundOnChange}
                placeholder="Enter transaction ID..."
                className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#715EFE]/20 focus:border-[#715EFE] transition-all duration-200"
              />
            </div>
          </div>

          {/* Warning Message */}
          <div className="flex items-start gap-3 p-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl">
            <FiAlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Please confirm before refunding</p>
              <p className="text-xs text-amber-600 mt-0.5">This action cannot be undone. The amount will be returned to the user's account.</p>
            </div>
          </div>

          {/* Form Validity Indicator */}
          {!isFormValid && (
            <div className="flex items-center gap-3 p-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl animate-in fade-in duration-200">
              <FiAlertCircle className="w-5 h-5 text-rose-500 flex-shrink-0" />
              <p>Please fill in all required fields to process the refund</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse sm:flex-row items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0 bg-gray-50/50 rounded-b-2xl">
          <button
            className="w-full sm:w-auto px-6 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-all duration-200"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className={`w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-medium text-white rounded-lg transition-all duration-200 ${
              isFormValid && !loading
                ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:shadow-lg hover:shadow-amber-500/25 hover:-translate-y-0.5'
                : 'bg-gray-300 cursor-not-allowed opacity-70'
            }`}
            onClick={onSubmit}
            disabled={!isFormValid || loading}
          >
            {loading ? (
              <>
                <FiLoader className="w-4 h-4 animate-spin" />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <FiArrowUp className="w-4 h-4" />
                <span>Refund Now</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default RefundModal